import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { NAV_LINKS, ROOMS_ROUTE, SITE_NAME } from '@/lib/constants'
import type { Customer } from '../api/types'

interface MobileNavPanelProps {
  isOpen: boolean
  onClose: () => void
  customer: Customer | null
  onLogout: () => void
}

function MobileNavPanel({ isOpen, onClose, customer, onLogout }: MobileNavPanelProps) {
  const { t } = useTranslation()
  const location = useLocation()

  useEffect(() => {
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70"
            style={{ zIndex: 99998 }}
            onClick={onClose}
          />

          {/* Slide-in drawer — navy background matching real site nav */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-72 overflow-y-auto shadow-xl"
            style={{ zIndex: 99999, backgroundColor: '#0F2966' }}
          >
            {/* Header row */}
            <div
              className="flex items-center justify-between p-4"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}
            >
              <Link to="/" onClick={onClose} aria-label={SITE_NAME}>
                <img
                  src="/images/nuva/logo/logo-header.png"
                  alt={SITE_NAME}
                  style={{ height: 50, width: 'auto', objectFit: 'contain' }}
                />
              </Link>
              <button
                onClick={onClose}
                className="p-2"
                style={{ color: '#FFFFFF' }}
                aria-label={t('common.close', { defaultValue: 'Cerrar' })}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Nav links — same 5 as desktop */}
            <nav className="flex flex-col py-2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={onClose}
                  className="px-6 py-4 text-base font-bold transition-colors"
                  style={{
                    color: '#FFFFFF',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    fontFamily: 'Montserrat, sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#F3EADB'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#FFFFFF'
                  }}
                >
                  {t(link.labelKey)}
                </Link>
              ))}

              {/* Account link when logged in */}
              {customer && (
                <>
                  <Link
                    to="/account"
                    onClick={onClose}
                    className="px-6 py-4 text-base font-bold transition-colors"
                    style={{
                      color: '#FFFFFF',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    {t('nav.account', { defaultValue: 'Mi Cuenta' })}
                  </Link>
                  <button
                    onClick={() => {
                      onLogout()
                      onClose()
                    }}
                    className="px-6 py-4 text-left text-base font-bold transition-colors"
                    style={{
                      color: '#FFFFFF',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    {t('nav.logout', { defaultValue: 'Salir' })}
                  </button>
                </>
              )}
            </nav>

            {/* Reservar CTA */}
            <div className="p-6">
              <Link
                to={ROOMS_ROUTE}
                onClick={onClose}
                className="flex w-full items-center justify-center text-sm font-bold transition-colors"
                style={{
                  height: 50,
                  borderRadius: 100,
                  backgroundColor: 'transparent',
                  border: '1px solid #FFFFFF',
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, sans-serif',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget
                  el.style.backgroundColor = '#F3EADB'
                  el.style.color = '#0F2966'
                  el.style.borderColor = '#F3EADB'
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget
                  el.style.backgroundColor = 'transparent'
                  el.style.color = '#FFFFFF'
                  el.style.borderColor = '#FFFFFF'
                }}
              >
                {t('nav.book')}
              </Link>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export function MobileNav({
  customer,
  onLogout,
}: {
  customer: Customer | null
  onLogout: () => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <div className="lg:hidden">
      <button
        onClick={() => setIsOpen(true)}
        className="p-2"
        style={{ color: '#FFFFFF' }}
        aria-label={t('common.open_menu', { defaultValue: 'Abrir menú' })}
      >
        <Menu className="h-6 w-6" />
      </button>

      {mounted &&
        createPortal(
          <MobileNavPanel
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            customer={customer}
            onLogout={onLogout}
          />,
          document.body,
        )}
    </div>
  )
}
